// TYPE-ONLY, all three. This module is the seam between the canvas's style SOURCES and the artifact
// renderer, so it must know their shapes and none of their code: a type import is erased, which keeps this
// file a leaf that the generation hook, a test, or a future theme surface can import without dragging the
// canvas in behind it. `styleImport.ts` takes exactly the same care with `BrandKit`, for the same reason.
import type { BrandStyle, MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
import type { BrandKit } from '~/design-system/components/DeckGen/birtvy/brandKit'
import type { BrandKitBinding } from '~/design-system/components/DeckGen/birtvy/brandKitDefault'
import { styleFromBrandKit, styleFromBrandStyle, styleFromMoodboardProfile } from './styleImport'
import { mergeStyles, type Style } from './styleRecord'
import { styleToThemeRecipe } from './styleRender'

/**
 * ARTIFACT STYLE RESOLUTION — every style source a doc slide can have, settled into one {@link Style} and
 * one theme recipe.
 *
 * WHY THIS FILE EXISTS. `useBirtvyDocGeneration` used to build an artifact's theme like this:
 *
 *   brandStyleToTokens({ ...wiredWebStyle, logoUrl: undefined }) → buildBrandStyleBlock(tokens, 100)
 *
 * Three things were wrong with it, all measured (2026-08-12/13 audit):
 *
 *  1. `buildBrandStyleBlock` is the SLIDE serializer. It writes prose about a palette for a model that
 *     invents its own CSS. An artifact declares seven `--a-*` custom properties in `:root` and reaches every
 *     colour through `var()`, and that vocabulary is what `styleToArtifactVars` / `styleToThemeRecipe`
 *     already speak. The slide block cannot state a token, so nothing ever did.
 *  2. NOTHING REPAIRED THE COLOUR ROLES. granola.ai's scrape labelled `text: #ffdef6` on `bg: #ffffff` —
 *     1.23:1, unreadable. Slides survived it because the flow model reads the block as prose and quietly
 *     substituted black; an artifact obeys `--a-text` literally, so this is precisely the path where the
 *     fault lands. `styleFromBrandStyle` now contrast-repairs at import, so the record never carries it.
 *  3. NOTHING RECONCILED FONT NAMES. granola declares "Quadrant Notepad" and ships an `@font-face` for
 *     "Quadrant", so every heading fell back to a system sans — invisibly, because the BODY font matched
 *     exactly. `styleFromBrandStyle` cross-checks families against the shipped faces.
 *
 * AND THE BIGGER GAP: the deck's own brand kit never reached an artifact AT ALL. `designSystems`,
 * `activeDesignSystemId` and `brandKitBinding` are hydrated into canvas state and feed the SLIDE path, and
 * the artifact path never looked at them. A user with a brand kit got artifacts that ignored it.
 *
 * PURE, on purpose: no React, no fetch, no clock, no randomness. The precedence below is the whole point of
 * the file, and a precedence you cannot assert on in a unit test is a precedence that drifts. The hook stays
 * a caller.
 *
 * IMAGES ARE OUT OF SCOPE AND STAY OUT. `ThemeRecipeOptions.assets` (the site harvest: real photographs and
 * CSS treatments off the brand's page) is deliberately never passed from here. The production contract
 * (`birtvyOpen/artifactContract.ts`) bans remote images outright and that is Guri's call to relax, not this
 * layer's. THE MARK IS THE ONE THING THAT DOES COME THROUGH — see {@link ARTIFACT_STYLE_STRENGTH} and the
 * logo note on {@link resolveArtifactStyle}.
 *
 * @see styleRecord.ts   the contract, and `mergeStyles`, which is how precedence is decided here
 * @see styleRender.ts   `styleToThemeRecipe`, the artifact's only door
 */

/**
 * How forcefully an artifact is told to adopt the resolved theme, on the product's own 0–100 dial.
 *
 * 100 = `strict`, which is byte-for-byte what the replaced call passed (`buildBrandStyleBlock(tokens, 100)`).
 * The deck's theme is not a mood board here: a wired site or a saved kit is a stated instruction, and the
 * artifact contract carries no aesthetics of its own precisely so that this recipe can own them.
 */
export const ARTIFACT_STYLE_STRENGTH = 100

/** Everything a single artifact generation knows about how it should look. All sources are optional
 *  because a real deck is usually missing most of them, and absence must cost nothing. */
export interface ArtifactStyleSources {
  /** The brand of a WEBSITE card wired to this slide — CSS read off a real page, so `measured`. */
  wiredWebStyle?: BrandStyle | null
  /** The deck's standing style: its active design system, or the kit snapshot it was generated from (see
   *  {@link brandKitFromBinding}). Resolving WHICH kit is the caller's job; ranking it is this file's. */
  brandKit?: BrandKit | null
  /**
   * The EXTRACTED style of image(s) wired to this slide in STYLE mode — a MoodboardProfile synthesized by
   * /deckgen-moodboard-api, entering as `inferred` (a vision model read pixels; pixels cannot name a font
   * or state an exact hex the way CSS can). This is Guri's 2026-08-16 spec for artifacts: the image is
   * EXTRACTED into the recipe, never shown to the generating model — the artifact's "different way" from
   * the slide path's vision reference. Extraction/caching is the caller's job; ranking is this file's.
   */
  wiredImageProfile?: MoodboardProfile | null
  /** How many wired style images produced the profile — 1 reads as `image`, several as `moodboard`, which
   *  is only about naming the source honestly in the record and the precedence line. */
  wiredImageCount?: number
  /** The deck's own artifact theme recipe — free prose the user typed in the theme modal. Passed through
   *  untouched: it is not a style source, it is the deck's voice. */
  docTheme?: string
}

/**
 * What one generation call knows about its own request, beyond the sources.
 *
 * `reserve` is the caller's promise about what it appends AFTER the recipe this returns — the kit page's
 * hit-testing law, a rules block — so the recipe is fitted to leave room for it; the precedence line this
 * file appends itself is reserved here, not by the caller. `ground` is threaded to the kit importer and is
 * a no-op until Phase C. `docCount` turns on the set rules (2+). `lock` defaults on; `false` is for a
 * caller deliberately asking for a different system.
 */
export interface ArtifactStyleOptions {
  reserve?: number
  ground?: string
  docCount?: number
  lock?: boolean
}

/**
 * Room for {@link precedenceLine}, which this file appends after the fitted recipe. The longest form ("The
 * theme above is the deck's brand kit. It WINS over anything earlier in this recipe: use its tokens, roles
 * and fonts.") is under 130 characters including the blank line before it; a site-plus-image-plus-kit
 * sentence is longer and rare, and a few characters over the route's clamp cost the END of that sentence,
 * never a token.
 */
const PRECEDENCE_RESERVE = 130

export interface ArtifactStyleResult {
  /**
   * The settled record, or null when no source carried a style. Returned beside the recipe because the
   * same resolution answers other questions — which tokens a deterministic re-theme should swap, what the
   * inspector should show — and re-deriving it at each of those call sites is how two answers diverge.
   */
  style: Style | null
  /** The `themeRecipe` for `/birtvy-open-api`, ready to send. Empty string when there is nothing to say. */
  recipe: string
}

/**
 * PRECEDENCE, as a decision rather than an accident.
 *
 * A WIRED WEBSITE OUTRANKS THE DECK'S BRAND KIT. Wiring a site to this slide is an explicit instruction
 * about THIS artifact; the kit is the deck's standing default. The same order already holds on the slide
 * path ("wired refs always win", `useBirtvySlideGeneration`), and an artifact that ignored the site you
 * just wired to it would be the more surprising of the two behaviours.
 *
 * THE KIT IS NOT DISCARDED, IT FILLS THE GAPS. That is what `mergeStyles` is for, and why this is a merge
 * and not an if-chain: a website scrape gives colour and type and never a layout pattern, a tone or a set
 * of "avoids", while a kit's profile is mostly those. Ranking by CONFIDENCE rather than by call order also
 * means the kit's own `brandStyle` half (exact CSS, `measured`) is not thrown away by a scrape's `inferred`
 * font repair. Ties go to the later argument, which is why the site is `incoming`: on a field both sources
 * measured, the one wired to this slide wins.
 *
 * THE RECIPE IS ADDITIVE AND `docTheme` STAYS FIRST. The deck's typed prose is its own voice and it is
 * older than whatever got wired today; the token block that follows says what it does not say. The
 * precedence line after the block is the one sentence that settles a genuine collision — it is the same
 * move (and nearly the same words) the replaced code made when it appended the brand block.
 *
 * THE LOGO NOW COMES THROUGH. The replaced call stripped `logoUrl` because `buildBrandStyleBlock` would
 * emit `<img src="…">` into a document whose contract bans remote images. `styleToThemeRecipe` states the
 * mark in the artifact's own terms — placed once, in flow, with the contrast rule and the "nothing in the
 * layout may depend on it" guard — and names the exemption out loud: a URL the deck ALREADY HOLDS was not
 * composed by the model, which is the exact thing the contract's srcless `data-logo` dance exists to
 * prevent. So the mark is no longer stripped on the way in. Everything else remote still is.
 *
 * A NOTE ON WHAT IS NOT RE-AUDITED HERE. `styleFromBrandStyle` and `styleFromBrandKit` each repair their
 * own colour roles, but a merge can still take `bg` from one half and `text` from the other, and two
 * individually-legible sources can compose into an unreadable pair. The repair is module-private to
 * `styleImport.ts`, so this file does not run it — and it does not need to: `styleToThemeRecipe` measures
 * `--a-text` / `--a-muted` against the emitted ground and WITHHOLDS a role it cannot read, then tells the
 * document to choose one and says why. A composed fault degrades to a stated gap, never to pale pink.
 */
export function resolveArtifactStyle(
  sources: ArtifactStyleSources,
  opts: ArtifactStyleOptions = {},
): ArtifactStyleResult {
  const deckRecipe = (sources.docTheme ?? '').trim()

  // `ground` reaches the importer today and changes nothing there yet: Phase C's `groundStyle` fills it.
  const kitStyle = sources.brandKit ? styleFromBrandKit(sources.brandKit, { ground: opts.ground }) : null
  const siteStyle = sources.wiredWebStyle ? styleFromBrandStyle(sources.wiredWebStyle) : null
  // Wired style image(s), between kit and site deliberately: an explicit wire on THIS slide outranks the
  // deck's standing kit (same doctrine as the site), but a site's measured CSS still beats the image's
  // inferred read on any field both state — confidence settles that, not order. Ties among peers go to
  // the later argument, so image-over-kit and site-over-image both fall out of the merge chain below.
  const imageStyle = sources.wiredImageProfile
    ? styleFromMoodboardProfile(
        sources.wiredImageProfile,
        (sources.wiredImageCount ?? 1) > 1 ? 'moodboard' : 'image',
        { name: 'Wired image style', origin: 'wired-images' },
      )
    : null
  const kitWithImage = kitStyle && imageStyle ? mergeStyles(kitStyle, imageStyle) : (imageStyle ?? kitStyle)
  const style = kitWithImage && siteStyle ? mergeStyles(kitWithImage, siteStyle) : (siteStyle ?? kitWithImage)

  // NO SOURCES → the recipe is the deck's prose and nothing else, byte for byte what this path sent before
  // the record existed. `''` when there is no prose either, which the caller reads as "send no theme at
  // all" — and an artifact with no THEME CONTEXT is instructed by the contract to commit to an aesthetic of
  // its own, which is the behaviour an unstyled deck has always had.
  if (!style) return { style: null, recipe: deckRecipe }

  // The sizes ride INSIDE the block now, in `## Type`, fitted with everything else. They used to be a
  // separate block appended after this fit, gated on a scale being stored, and a rich kit cut them
  // mid-sentence at the route's clamp — the type scale is on the record (`type.roles`) precisely so it is
  // budgeted like a token rather than tacked on behind the budget.
  const block = styleToThemeRecipe(style, {
    strength: ARTIFACT_STYLE_STRENGTH,
    reserve: (opts.reserve ?? 0) + PRECEDENCE_RESERVE,
    ...(opts.docCount != null ? { docCount: opts.docCount } : {}),
    ...(opts.lock != null ? { lock: opts.lock } : {}),
  })
  // A Style with nothing sayable (an empty kit, a scrape that found no colour and no font) renders to ''.
  // Appending a precedence sentence about a block that is not there would be an instruction pointing at
  // nothing, so the deck's prose stands alone exactly as in the no-source case.
  if (!block) return { style, recipe: deckRecipe }

  return {
    style,
    recipe: [deckRecipe, `${block}\n\n${precedenceLine(Boolean(siteStyle), Boolean(kitStyle), Boolean(imageStyle))}`]
      .filter(Boolean)
      .join('\n\n'),
  }
}

/**
 * The one sentence that settles a collision between the deck's typed prose and the resolved theme.
 *
 * Near-verbatim from the line the wired-website branch already shipped ("The wired website's brand above
 * WINS over anything earlier in this recipe: use its palette, roles and fonts"), because that phrasing has
 * been read by real generations and rewriting proven wording means re-learning which sentences a model
 * obeys. What changed is the SUBJECT: the theme above may now be a kit, a site, or a site standing on a
 * kit, and naming which one is what lets the model tell an instruction from a default.
 */
function precedenceLine(fromSite: boolean, fromKit: boolean, fromImage: boolean): string {
  // Name the LEAD source, then whoever is filling gaps behind it — the model needs to tell an
  // instruction from a default, not read a bibliography. Site leads when present (CSS is measured),
  // then the wired image's extraction, then the kit.
  const fillers = [
    fromSite && fromImage ? "the wired image's extracted style" : null,
    fromKit && (fromSite || fromImage) ? "the deck's brand kit" : null,
  ].filter(Boolean)
  // The filler clause names its subject ("what the site does not state"), and for site+kit that keeps
  // the sentence BYTE-IDENTICAL to the one real generations have obeyed since the wired-website branch
  // shipped — the precedence test pins it, and rewriting proven wording means re-learning what a model
  // listens to.
  const filling = fillers.length
    ? `, with ${fillers.join(' and ')} filling what the ${fromSite ? 'site' : 'image'} does not state`
    : ''
  const source = fromSite
    ? `The theme above was read from the website wired to this slide${filling}.`
    : fromImage
      ? `The theme above was extracted from the image wired to this slide${filling}.`
      : "The theme above is the deck's brand kit."
  return `${source} It WINS over anything earlier in this recipe: use its tokens, roles and fonts.`
}

/**
 * The deck's KIT SNAPSHOT as a kit.
 *
 * A deck generated from the user's default brand kit records a {@link BrandKitBinding} rather than a kit
 * id: the snapshot is what keeps the promise "editing a kit never changes a deck you already made". So for
 * most real decks the binding is the ONLY style the deck has, and an artifact that read only
 * `designSystems` would still ignore the brand. Returns null for no binding, so the caller can write
 * `resolveActiveBrandKit(...) ?? brandKitFromBinding(...)` and get the slide path's own fallback order.
 *
 * WHAT USED TO BE LOST IN THE CROSSING, and no longer is. A binding's exact-CSS half is
 * `BrandStyleTokens`, which carries a derived `headingFontWeights`; a `BrandKit`'s is a `BrandStyle`,
 * which carries the `fontFaces` those weights were read from — and, now, a `typeScale`. Neither fits in
 * the tokens shape, so both used to drop here, silently, on the path MOST real decks take.
 *
 * That was not the small loss the original note assumed. A deck generated from the default kit arrived
 * with its palette and its mark and NOTHING describing its hierarchy, so every artifact invented its own
 * sizes — measured on Guri's own canvas, 2026-08-18: nine distinct font-sizes in one document, none of
 * them the brand's. It read as "the brand kit does nothing", because visibly it did almost nothing.
 *
 * So the binding carries both in their own right (see `BrandKitBinding`) and this restores them. The
 * faux-bold guard's line ("this face ships only weight 400") works on a binding-styled artifact again,
 * and the type scale reaches the recipe.
 */
export function brandKitFromBinding(binding: BrandKitBinding | null | undefined): BrandKit | null {
  if (!binding) return null
  const tokens = binding.brandStyle
  // Built by spread rather than by assignment so an absent optional stays ABSENT: `styleFromBrandStyle`
  // reads presence, and `{ headingFont: undefined }` is a key that exists.
  const brandStyle: BrandStyle | undefined =
    tokens || binding.typeScale || binding.fontFaces?.length
      ? {
          palette: tokens?.palette ?? [],
          roles: tokens?.roles ?? {},
          ...(tokens?.headingFont ? { headingFont: tokens.headingFont } : {}),
          ...(tokens?.bodyFont ? { bodyFont: tokens.bodyFont } : {}),
          ...(tokens?.typography ? { typography: tokens.typography } : {}),
          ...(tokens?.logoUrl ? { logoUrl: tokens.logoUrl } : {}),
          // The two the binding now carries in their own right. Without these a deck generated from the
          // default kit arrived brand-coloured with no hierarchy and no real typeface, and every artifact
          // invented its own sizes — the failure that made a granola deck and a stock deck the same deck.
          ...(binding.typeScale ? { typeScale: binding.typeScale } : {}),
          ...(binding.fontFaces?.length ? { fontFaces: binding.fontFaces } : {}),
        }
      : undefined
  return {
    id: binding.kitId,
    name: binding.kitName,
    profile: binding.profile,
    ...(brandStyle ? { brandStyle } : {}),
    // The binding records ONE timestamp — the generation it belongs to. Both kit dates take it: this is a
    // projection for one generation call, never something that gets saved back over a real kit.
    createdAt: binding.at,
    updatedAt: binding.at,
  }
}
